import { useCallback, useRef, useState } from 'react'
import { imageToDataURL, isLocalUrl, revokeBlobUrl, signedUrlToBlobUrl } from 'utils/image'

import { FileMimetype } from 'constants/misc'

const MAX_HISTORY_SIZE = 50

export enum PhotoStudioHistorySource {
  ORIGINAL = 'original',
  MAGIC_ERASER = 'magicEraser',
  VIRTUAL_STAGING = 'virtualStaging',
  ADJUST = 'adjust',
  DISCLAIMER = 'disclaimer',
  ENHANCE = 'enhance',
}

export interface PhotoStudioHistoryEntry {
  id: string
  imageUrl: string
  imageData: string // Data URL for the image
  createdAt: number
  source: PhotoStudioHistorySource
  imageGenerationSequenceNumber?: number // Which generation number from the original image
  editionSettings?: Record<string, unknown> // Snapshot of settings at generation time for accurate save analytics
  /** Lab SDK results: the image this version was generated from (the "Before" of Compare and of the run-source menu) */
  sourceImageUrl?: string
}

export interface PhotoStudioHistoryState {
  canUndo: boolean
  canRedo: boolean
  current?: PhotoStudioHistoryEntry
  entries: PhotoStudioHistoryEntry[]
}

/**
 * Revoke the images of removed versions that nothing shows any more. A surviving Lab result may still use one as its
 * source (the Before of Compare and of the Generate chevron), and the original's local copy stays on screen in the
 * Original view whatever happens to the history.
 */
const releaseImages = (removed: PhotoStudioHistoryEntry[], surviving: PhotoStudioHistoryEntry[], originalImageUrl?: string) => {
  const inUse = new Set<string>()
  surviving.forEach(entry => {
    inUse.add(entry.imageUrl)
    if (entry.sourceImageUrl) inUse.add(entry.sourceImageUrl)
  })
  if (originalImageUrl) inUse.add(originalImageUrl)
  removed.forEach(entry => {
    if (!inUse.has(entry.imageUrl)) revokeBlobUrl(entry.imageUrl)
  })
}

/**
 * Photo Studio history hook for tracking picture versions.
 * Tracks versions when:
 * - Generate button succeeds (Magic Eraser, Virtual Staging)
 * - Disclaimer is added/removed
 * - Leaving Pintura for another tool
 * 
 */
export const usePhotoStudioHistory = (
  initialImageUrl: string,
  setPreviewImage: (url: string) => void,
  /** The original resolved to a local copy, once available — what the preview starts on */
  originalImageUrl?: string
) => {
  // History is stored as an array with a cursor pointing to current position
  const historyRef = useRef<PhotoStudioHistoryEntry[]>([{
    id: 'initial',
    imageUrl: initialImageUrl,
    imageData: '',
    createdAt: Date.now(),
    source: PhotoStudioHistorySource.ORIGINAL,
  }])

  const [cursor, setCursor] = useState(0)

  // The first version shows the original's local copy once it exists: the preview starts on that copy, so the first
  // pre-run snapshot is recognised as the current version instead of being recorded twice, and undo back to the
  // original no longer re-fetches its (expiring) URL.
  const originalImageUrlRef = useRef(originalImageUrl)
  originalImageUrlRef.current = originalImageUrl
  const initialEntry = historyRef.current[0]
  if (originalImageUrl && originalImageUrl !== initialImageUrl && initialEntry?.id === 'initial' && initialEntry.imageUrl === initialImageUrl) {
    historyRef.current = [{ ...initialEntry, imageUrl: originalImageUrl }, ...historyRef.current.slice(1)]
  }

  /**
   * Add a new version to history.
   * This is called after a successful operation (generate, disclaimer change, pintura change to another tool).
   */
  const addVersion = useCallback(async (
    imageUrl: string,
    source: PhotoStudioHistorySource,
    imageGenerationSequenceNumber?: number,
    editionSettings?: Record<string, unknown>,
    sourceImageUrl?: string,
    signal?: AbortSignal
  ): Promise<string> => {
    signal?.throwIfAborted()
    // Convert signed URLs to blob URLs for history storage
    // so undo/redo are instant (no re-fetching expired signed URLs)
    const blobImageUrl = isLocalUrl(imageUrl)
      ? imageUrl
      : await signedUrlToBlobUrl(imageUrl, {
        component: 'usePhotoStudioHistory',
        action: 'addVersion'
      })

    // Canvas conversion can finish after the Studio has closed. Check before committing.
    let imageData: string
    try {
      signal?.throwIfAborted()
      imageData = await imageToDataURL(blobImageUrl, {
        format: FileMimetype.IMAGE_JPEG,
        logContext: {
          component: 'usePhotoStudioHistory',
          action: 'addVersion'
        }
      })
      signal?.throwIfAborted()
    } catch (error) {
      // Only release URLs created here; callers own their existing local images.
      if (blobImageUrl !== imageUrl) revokeBlobUrl(blobImageUrl)
      throw error
    }

    const entry: PhotoStudioHistoryEntry = {
      id: `version-${Date.now()}-${Math.random().toString(16).slice(2)}`,
      imageUrl: blobImageUrl,
      imageData,
      createdAt: Date.now(),
      source,
      imageGenerationSequenceNumber,
      editionSettings,
      sourceImageUrl,
    }

    // Get the current entry at cursor position
    const currentEntry = historyRef.current[cursor]
    const currentPreviewUrl = currentEntry?.imageUrl

    // Check for duplicate - if the image we're adding is the same as current, skip
    if (currentPreviewUrl === blobImageUrl) {
      return blobImageUrl
    }

    // Determine the base history to use
    let base: PhotoStudioHistoryEntry[]
    let removed: PhotoStudioHistoryEntry[] = []

    if (cursor < historyRef.current.length - 1) {
      // We're in the middle of history (after undo).
      // When user undoes and then makes a new change, they're creating a new branch.
      // We should drop all "redo" versions (entries after cursor) and add the new version.
      // This is standard undo/redo behavior - once you make a new change after undoing,
      // you lose the ability to redo to the old future versions.
      base = historyRef.current.slice(0, cursor + 1)
      removed = historyRef.current.slice(cursor + 1)
    } else {
      // We're at the end of history - just use the full history
      base = historyRef.current
    }

    // Add new entry and move cursor forward
    const newHistory = [...base, entry]

    // Prune oldest entries if history exceeds max size
    if (newHistory.length > MAX_HISTORY_SIZE) {
      removed = [...removed, ...newHistory.splice(0, newHistory.length - MAX_HISTORY_SIZE)]
    }

    historyRef.current = newHistory
    releaseImages(removed, newHistory, originalImageUrlRef.current)
    setCursor(historyRef.current.length - 1)
    setPreviewImage(blobImageUrl)
    return blobImageUrl
  }, [cursor, setPreviewImage])

  /**
   * Undo: go back one version
   */
  const undo = useCallback((): void => {
    if (cursor === 0) {
      return
    }

    const newIndex = cursor - 1
    setCursor(newIndex)
    const entry = historyRef.current[newIndex]
    setPreviewImage(entry.imageUrl)
  }, [cursor, setPreviewImage])

  /**
   * Redo: go forward one version
   */
  const redo = useCallback((): void => {
    if (cursor >= historyRef.current.length - 1) {
      return
    }

    const newIndex = cursor + 1
    setCursor(newIndex)
    const entry = historyRef.current[newIndex]
    setPreviewImage(entry.imageUrl)
  }, [cursor, setPreviewImage])

  /**
   * Revert to the previous version (used when generation fails)
   */
  const revertToPreviousVersion = useCallback((): void => {
    if (cursor === 0) {
      return
    }

    const previousIndex = cursor - 1
    setCursor(previousIndex)
    const entry = historyRef.current[previousIndex]
    setPreviewImage(entry.imageUrl)
  }, [cursor, setPreviewImage])

  /**
   * Reset history to initial state
   */
  const resetHistory = useCallback((url?: string) => {
    const resolvedUrl = url || initialImageUrl
    historyRef.current = [{
      id: 'initial',
      imageUrl: resolvedUrl,
      imageData: '',
      createdAt: Date.now(),
      source: PhotoStudioHistorySource.ORIGINAL,
    }]
    setCursor(0)
    setPreviewImage(resolvedUrl)
  }, [initialImageUrl, setPreviewImage])

  const historyState: PhotoStudioHistoryState = {
    canUndo: cursor > 0,
    canRedo: cursor < historyRef.current.length - 1,
    current: historyRef.current[cursor],
    entries: historyRef.current,
  }

  return {
    historyState,
    addVersion,
    undo,
    redo,
    revertToPreviousVersion,
    resetHistory,
  }
}
